import { type FC, type SubmitEvent, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
    Button,
    ButtonColour,
    ButtonStyle,
    Input,
    TextArea,
} from "@onshopfront/components";
import RetractableInput from "#/Common/Forms/RetractableInput";
import type { UserType } from "../../Actions/UserActions";
import BoxForm from "../../Common/BoxForm/BoxForm";
import type { RootState } from "../../Reducers/DeveloperReducers";
import { useTitle } from "../../Utilities/TitleHook";
import { PartnerRegistrationBackground } from "./PartnerRegistrationBackground";

class UserAlreadyRegisteredError extends Error {
    constructor() {
        super("User already registered");
        this.name = "UserAlreadyRegisteredError";
    }
}

const PartnerRegistration: FC = () => {
    const user = useSelector<RootState, boolean | UserType>(state => state.user.user);

    const navigate = useNavigate();
    const [ error, setError ] = useState<null | string>(null);
    const [ loading, setLoading ] = useState(false);
    const [ registrationStatus, setRegistrationStatus ] = useState(
        typeof user !== "boolean" ? user.partner_status : "not-registered"
    );

    useTitle("Partner Registration");

    const handleSubmit = (e: SubmitEvent<HTMLFormElement>): void => {
        e.preventDefault();

        const form = e.currentTarget;

        const isValid = form.checkValidity();

        setError(isValid ? null : "Please fix the validation errors");
        setLoading(isValid);

        if(!isValid) {
            return;
        }

        const data: Record<string, string> = {};

        for(let i = 0, l = form.elements.length; i < l; i++) {
            const el = form.elements[i] as HTMLInputElement;

            if(!el.name) {
                continue;
            } else if(el.type === "radio" && !el.checked) {
                continue;
            }

            data[el.name] = el.value;
        }

        fetch(form.action, {
            method : "POST",
            headers: {
                "Content-Type": "application/json",
                "Accept"      : "application/json",
            },
            body: JSON.stringify(data),
        })
            .then(async response => {
                if(!response.ok) {
                    if(response.status === 403) {
                        throw new UserAlreadyRegisteredError();
                    }

                    throw await response.json();
                }

                return response.json();
            })
            .then(() => {
                navigate("/partner/registered");
            })
            .catch(err => {
                console.error("Could not submit partner registration", err);

                if(err instanceof UserAlreadyRegisteredError) {
                    setRegistrationStatus("registered");
                } else if(err.message) {
                    setError(err.message);
                } else {
                    setError("Please try again or contact us at developers@shopfront.com.au");
                }

                setLoading(false);
            });
    };

    if(typeof user === "boolean") {
        return (
            <main>
                <p>Please log in to register as a partner</p>
            </main>
        );
    }

    let contents;

    if(registrationStatus === "approved") {
        contents = (
            <p>You&apos;ve already been approved as a developer partner.</p>
        );
    } else if(registrationStatus === "registered") {
        contents = (
            <p>
                You&apos;ve already registered to be a partner, we&apos;ll get
                back to you with any feedback or if you&apos;ve been approved.
            </p>
        );
    } else {
        contents = (
            <>
                <p>
                    Please fill in the following information to express interest
                    in becoming a partner.
                </p>
                <form method="post" action="/partner/register" onSubmit={handleSubmit}>
                    <RetractableInput hasValue label="Company Name">
                        <Input
                            type="text"
                            name="company"
                            placeholder="Company PTY LTD"
                            required
                        />
                    </RetractableInput>
                    <RetractableInput hasValue label="Your Name">
                        <Input
                            type="text"
                            name="name"
                            placeholder="John Smith"
                            defaultValue={user.name}
                            required
                        />
                    </RetractableInput>
                    <RetractableInput hasValue label="Your Email">
                        <Input
                            type="email"
                            name="email"
                            placeholder="john.smith@company.com.au"
                            defaultValue={user.email}
                            required
                        />
                    </RetractableInput>
                    <RetractableInput hasValue label="Application / Company Website">
                        <Input
                            type="text"
                            name="website"
                            placeholder="https://company.com.au"
                            required
                        />
                    </RetractableInput>
                    <RetractableInput hasValue label="Application / Company Description">
                        <TextArea
                            name="description"
                            placeholder="Shopfront is a cloud-based retail solution system that makes it easy to..."
                            required
                        />
                    </RetractableInput>
                    <table className="table-fixed">
                        <tbody>
                            <tr>
                                <td>Do you currently have customers on Shopfront?</td>
                                <td className="w-16">
                                    <div>
                                        <label>
                                            <input
                                                type="radio"
                                                value="yes"
                                                name="hasCustomers"
                                                className="mr-2"
                                            />Yes
                                        </label>
                                    </div>
                                    <div>
                                        <label>
                                            <input
                                                type="radio"
                                                value="no"
                                                name="hasCustomers"
                                                defaultChecked
                                                className="mr-2"
                                            />No
                                        </label>
                                    </div>
                                </td>
                            </tr>
                            <tr>
                                <td>
                                    Have you already completed your integration with Shopfront?
                                </td>
                                <td className="w-16">
                                    <div>
                                        <label>
                                            <input
                                                type="radio"
                                                value="yes"
                                                name="completedIntegration"
                                                className="mr-2"
                                            />Yes
                                        </label>
                                    </div>
                                    <div>
                                        <label>
                                            <input
                                                type="radio"
                                                value="no"
                                                name="completedIntegration"
                                                defaultChecked
                                                className="mr-2"
                                            />No
                                        </label>
                                    </div>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                    <div className="text-center">
                        <Button
                            style={ButtonStyle.Filled}
                            colour={ButtonColour.Primary}
                            type="submit"
                            disabled={loading}
                        >
                            {loading ? "Submitting..." : "Register"}
                        </Button>
                    </div>
                </form>
                <p>
                    Once you&apos;ve registered, Shopfront will review your application
                    and get in touch with you regarding next steps, this may take
                    several business days.
                </p>
            </>
        );
    }

    return (
        <main>
            <PartnerRegistrationBackground />
            <BoxForm
                error={error}
                onClear={() => {
                    setError(null);
                }}
            >
                <div className="partner-registration-form">
                    <h1>Partner Registration</h1>
                    {contents}
                </div>
            </BoxForm>
        </main>
    );
};

export default PartnerRegistration;
